import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, RotateCw } from "lucide-react";
import { EmailLog } from "@/hooks/useEmailLogs";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export const RETRYABLE_EMAIL_TYPES = ["confirmation", "modification", "cancellation"] as const;

export const isRetryable = (log: Pick<EmailLog, "status" | "email_type">) =>
  log.status === "failed" &&
  (RETRYABLE_EMAIL_TYPES as readonly string[]).includes(log.email_type);

const TYPE_LABELS: Record<string, string> = {
  confirmation: "Confirmation",
  modification: "Modification",
  cancellation: "Annulation",
};

interface RetryEmailDialogProps {
  log: EmailLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const RetryEmailDialog = ({ log, open, onOpenChange, onSuccess }: RetryEmailDialogProps) => {
  const [loading, setLoading] = useState(false);

  if (!log) return null;

  const handleRetry = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-manual-email", {
        body: {
          reservationId: log.reservation_id,
          emailType: log.email_type,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Email renvoyé à ${log.customer_email}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur inconnue";
      toast.error(`Échec de l'envoi : ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RotateCw className="w-4 h-4" />
            Réessayer l'envoi de l'email ?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <div className="space-y-1 rounded-md bg-muted p-3">
                <p>
                  <span className="font-medium text-foreground">Destinataire :</span>{" "}
                  {log.customer_name} &lt;{log.customer_email}&gt;
                </p>
                <p>
                  <span className="font-medium text-foreground">Type :</span>{" "}
                  {TYPE_LABELS[log.email_type] ?? log.email_type}
                </p>
                <p>
                  <span className="font-medium text-foreground">Réservation :</span>{" "}
                  {format(new Date(log.reservation_date), "PPP", { locale: fr })} à{" "}
                  {log.reservation_time.substring(0, 5)}
                </p>
              </div>
              <p className="text-muted-foreground">
                Un nouvel email sera envoyé immédiatement au client. Cette action ne peut pas être annulée.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleRetry();
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              "Envoyer"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
